import gql from "graphql-tag";
import {
    PaxNodeComplianceFieldsWithAuthFragmentFragment, PersonComplianceFragmentFragment
} from "__generated__/graphql/types";
import {useApolloClient, useQuery} from "react-apollo";
import {
    PaxNodeComplianceFieldsFragment,
    PaxNodeComplianceFieldsWithAuthFragment
} from "Queries/Compliance";
import {PersonComplianceFragment} from "../../Queries/Person";

export const PAXNODE_QUERY = gql`
    query ComplianceSwitcher_GetPaxNode($paxID: ID!){
        getPassenger(_id: $paxID){
            ...PaxNodeComplianceFieldsWithAuthFragment
        }
    }
    ${PaxNodeComplianceFieldsWithAuthFragment}
`

export const PERSON_QUERY = gql`
    query Compliance_GetPerson($personID: ID!){
        person(id: $personID){
            ...PersonComplianceFragment
        }
    }
    ${PersonComplianceFragment}
`

export function readPaxNodeFromCache(apolloClient: any, paxID: string): PaxNodeComplianceFieldsWithAuthFragmentFragment | null {
    try {
        return apolloClient.readFragment({
            id: 'PaxNode:' + paxID,
            fragment: PaxNodeComplianceFieldsFragment,
            fragmentName: 'PaxNodeComplianceFieldsFragment'
        });
    }
    catch (e) {
        console.warn("Could not read PaxNode from cache", e);
        return null;
    }
}

export function usePaxNodeQuery(paxID: string, cacheFirst: boolean = false, pollInterval: number = null) {

    const apolloClient = useApolloClient();
    const cachedPaxNode = cacheFirst ? readPaxNodeFromCache(apolloClient, paxID) : null;

    const {data, loading, error, refetch} = useQuery(PAXNODE_QUERY, {
        variables: {
            paxID: paxID
        },
        pollInterval: pollInterval,
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
        skip: !paxID || !!cachedPaxNode
    });

    if (cachedPaxNode) {
        return {
            paxNode: cachedPaxNode as PaxNodeComplianceFieldsWithAuthFragmentFragment,
            loading: false,
            error: undefined
        }
    }

    return {
        paxNode: data?.getPassenger as PaxNodeComplianceFieldsWithAuthFragmentFragment | undefined,
        loading: loading,
        error: error,
        refetch: refetch
    }
}

export function readPersonFromCache(apolloClient: any, personID: string): PersonComplianceFragmentFragment | null {
    try {
        return apolloClient.readFragment({
            id: 'Person:' + personID,
            fragment: PersonComplianceFragment,
            fragmentName: 'PersonComplianceFragment'
        });
    }
    catch (e) {
        console.warn("Could not read Person from cache", e);
        return null;
    }
}

export function usePersonQuery(personID: string, cacheFirst: boolean = false, pollInterval: number = null){
    const apolloClient = useApolloClient();
    const cachedPerson = cacheFirst ? readPersonFromCache(apolloClient, personID) : null;

    const {data, loading, error, refetch} = useQuery(PERSON_QUERY, {
        variables: {
            personID: personID
        },
        pollInterval: pollInterval,
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
        skip: !personID || !!cachedPerson
    });

    if (cachedPerson) {
        return {
            person: cachedPerson as PersonComplianceFragmentFragment,
            loading: false,
            error: undefined
        }
    }

    return {
        person: data?.person as PersonComplianceFragmentFragment | undefined,
        loading: loading,
        error: error,
        refetch: refetch
    }
}